import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createForm } from '../../services/api/formApi';

const CreateFormModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    settings: {
      collectEmail: false,
      allowMultipleResponses: false,
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError('Form title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const createdForm = await createForm({
        title: formData.title.trim(),
        description: formData.description.trim(),
        settings: formData.settings,
        questions: [], // Start with empty questions
      });

      // Close modal and navigate to edit the newly created form
      onClose();
      navigate(`/form/${createdForm.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} style={{ backgroundColor: isOpen ? 'rgba(15, 23, 42, 0.6)' : 'transparent', backdropFilter: 'blur(4px)' }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content border-0 rounded-4 shadow-lg overflow-hidden">
          <div className="modal-header border-bottom-0 p-4 pb-0">
            <h5 className="modal-title fw-bold font-heading h4">Create New Form</h5>
            <button type="button" className="btn-close" onClick={onClose} style={{ filter: 'grayscale(1)' }}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body p-4">
              <div className="mb-4">
                <label htmlFor="title" className="form-label fw-medium text-secondary small">FORM TITLE <span className="text-danger">*</span></label>
                <input
                  id="title"
                  type="text"
                  className="form-control form-control-lg bg-light border-0"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g. Customer Satisfaction Survey"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="description" className="form-label fw-medium text-secondary small">DESCRIPTION</label>
                <textarea
                  id="description"
                  className="form-control bg-light border-0"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe the purpose of this form..."
                  rows={3}
                  style={{ resize: 'none' }}
                />
              </div>

              <div className="card border-0 bg-primary-subtle bg-opacity-10 p-4 rounded-3">
                <h6 className="fw-bold text-primary mb-3 text-uppercase small" style={{ letterSpacing: '1px' }}>Settings</h6>

                <div className="d-flex flex-column gap-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input cursor-pointer"
                      type="checkbox"
                      id="collectEmail"
                      checked={formData.settings.collectEmail}
                      onChange={(e) => handleInputChange('settings.collectEmail', e.target.checked)}
                    />
                    <label className="form-check-label cursor-pointer" htmlFor="collectEmail">
                      Collect email addresses
                    </label>
                  </div>

                  {formData.settings.collectEmail && (
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        id="allowMultiple"
                        checked={formData.settings.allowMultipleResponses}
                        onChange={(e) => handleInputChange('settings.allowMultipleResponses', e.target.checked)}
                      />
                      <label className="form-check-label cursor-pointer" htmlFor="allowMultiple">
                        Allow multiple responses
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {error && <div className="alert alert-danger mt-4 border-0 shadow-sm">{error}</div>}
            </div>

            <div className="modal-footer border-top-0 p-4 pt-0">
              <button type="button" className="btn btn-light text-secondary fw-medium px-4" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary px-4 shadow py-2" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Creating...
                  </>
                ) : (
                  'Create Form'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateFormModal;
